



























































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { get as _get } from 'lodash';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import FHIRConfirmModal from './components/FHIRConfirmModal.vue';
import { isUserAllowed } from '@/utils/rbac.util';
import { PermissionsMatrixActionsEnum } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import { ToastProgrammatic as Toast } from 'buefy';
import FHIRSearchBox from './components/FHIRSearchBox.vue';
import FHIRClientsList from './components/FHIRClientsList.vue';
import EditFhirClientSidebar from './components/EditFhirClientSidebar.vue';
import {
  FHIRClient,
  FilteredFHIRUserPayload,
  GetFHIRClientsResponsePayload,
  GetFHIRRequestPayload,
  UpdateFHIRClientPayload
} from './types/admin-fhir-access-management.types';

const initialFilterState: FilteredFHIRUserPayload = {
  clientName: '',
  status: 'all'
};

@Component({
  computed: {
    PermissionsMatrixActionsEnum() {
      return PermissionsMatrixActionsEnum;
    }
  },
  methods: { isUserAllowed, _get },
  components: {
    BasePaginatorHoc,
    FHIRSearchBox,
    EditFhirClientSidebar
  }
})
export default class FHIRAccessManagementTab extends Vue {
  public page = 1;
  public perPage = 50;
  public sortColumn = 'client.name';
  public sortOrder: 'ASC' | 'DESC' = 'ASC';
  public filterParams: FilteredFHIRUserPayload = { ...initialFilterState };
  public isEditSidebarOpen = false;
  public editingClient: FHIRClient | undefined = undefined;

  @Action('fhir/getFhirClients')
  getFhirClients!: (params: GetFHIRRequestPayload) => void;

  @Action('fhir/updateFhirClient')
  updateFhirClient!: (args: {
    id: number;
    payload: UpdateFHIRClientPayload;
  }) => void;

  @Action('fhir/deleteFhirClient')
  deleteFhirClient!: (id: number) => void;

  @State(({ fhir }: RootState) => fhir.fhirClients)
  public fhirClients!: GetFHIRClientsResponsePayload;

  @State(({ fhir }: RootState) => fhir.apiState.getFhirClients)
  public getFhirClientsState!: ApiState;

  @State(({ fhir }: RootState) => fhir.apiState.updateFhirClient)
  public updateFhirClientState!: ApiState;

  @State(({ fhir }: RootState) => fhir.apiState.deleteFhirClient)
  public deleteFhirClientState!: ApiState;

  get FHIRClientsList() {
    return FHIRClientsList;
  }

  public mounted() {
    this.fetchClients();
  }

  private fetchClients() {
    this.getFhirClients({
      ...this.filterParams,
      page: this.page,
      limit: this.perPage,
      sortColumn: this.sortColumn,
      sortOrder: this.sortOrder
    });
  }

  public handleFilter() {
    this.page = 1;
    this.fetchClients();
  }

  public handleResetFilter() {
    this.filterParams = { ...initialFilterState };
    this.page = 1;
    this.fetchClients();
  }

  public handleSort(data: { sortColumn: string; sortOrder: 'ASC' | 'DESC' }) {
    this.sortColumn = data.sortColumn;
    this.sortOrder = data.sortOrder;
    this.fetchClients();
  }

  @Watch('page')
  @Watch('perPage')
  public onPaginationChange() {
    this.fetchClients();
  }

  public navigateToFHIRSettings() {
    this.$router.push({ name: 'fhir-settings' });
  }

  public openCreateClientModal() {
    this.$router.push({ name: 'fhir-client-create' });
  }

  public handleToggleStatus(item: FHIRClient) {
    const turningActive = !item.isActive;
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: turningActive
          ? 'Enable Client Access?'
          : 'Disable Client Access?',
        modalContent: turningActive
          ? 'This will allow the client to make API requests again.'
          : 'This will disable all API requests from this client.',
        confirmBtnText: turningActive
          ? 'Yes, Enable Access'
          : 'Yes, Disable Access',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.updateFhirClient({
            id: item.id,
            payload: { isActive: turningActive }
          });
        }
      }
    });
  }

  public handleDeleteClient(item: FHIRClient) {
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete Client?',
        modalContent:
          'This will permanently delete the client, including all credentials and access configurations. API access will be revoked immediately.',
        confirmBtnText: 'Confirm',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.deleteFhirClient(item.id);
        }
      }
    });
  }

  public handleViewClient(item: FHIRClient) {
    this.$router.push({
      name: 'fhir-client-detail',
      params: { id: String(item.id) }
    });
  }

  public handleEditClient(item: FHIRClient) {
    this.editingClient = item;
    this.isEditSidebarOpen = true;
  }

  public onSaveEdit(payload: UpdateFHIRClientPayload) {
    if (!this.editingClient) return;
    this.updateFhirClient({ id: this.editingClient.id, payload });
  }

  @Watch('getFhirClientsState.error')
  @isTruthy
  public onGetFhirClientsError() {
    Toast.open({
      message: _get(
        this.getFhirClientsState.error,
        'response.data.message',
        'Failed to load FHIR clients'
      ),
      position: 'is-top',
      type: 'is-danger'
    });
  }

  @Watch('updateFhirClientState.success')
  @isTruthy
  public onUpdateFhirClientSuccess() {
    Toast.open({
      message: 'Client status updated successfully.',
      position: 'is-top',
      type: 'is-success'
    });
  }

  @Watch('updateFhirClientState.error')
  @isTruthy
  public onUpdateFhirClientError() {
    Toast.open({
      message: _get(
        this.updateFhirClientState.error,
        'response.data.message',
        'Failed to update FHIR client.'
      ),
      position: 'is-top',
      type: 'is-danger'
    });
  }

  @Watch('deleteFhirClientState.success')
  @isTruthy
  public onDeleteFhirClientSuccess() {
    Toast.open({
      message: 'Client deleted successfully.',
      position: 'is-top',
      type: 'is-success'
    });
  }

  @Watch('deleteFhirClientState.error')
  @isTruthy
  public onDeleteFhirClientError() {
    Toast.open({
      message: _get(
        this.deleteFhirClientState.error,
        'response.data.message',
        'Failed to delete FHIR client.'
      ),
      position: 'is-top',
      type: 'is-danger'
    });
  }
}
